/**
 * Collection client pager (+ optional random shuffle).
 * Shopify Liquid cannot read custom query params (request.params does not exist),
 * so we render a large product window server-side and slice by ?hs_page= in the browser.
 * Uses normal full-page links (no pushState) so Dawn facets.js popstate stays consistent.
 * Re-inits when facets.js replaces ProductGridContainer (custom element upgrade).
 *
 * data-shuffle="true": Fisher–Yates with a session-stable seed so page 2/3 stay consistent
 * within the same browser tab, and each new visit gets a fresh order.
 */
if (!customElements.get('hs-bestsellers-pager')) {
  class HsBestsellersPager extends HTMLElement {
    connectedCallback() {
      this.perPage = Math.max(1, parseInt(this.dataset.perPage || '16', 10) || 16);
      this.grid = this.querySelector('[data-hs-bs-grid]') || this.querySelector('#product-grid');
      this.pager = this.querySelector('[data-hs-bs-pager]');
      if (!this.grid) return;
      if (this.dataset.shuffle === 'true') {
        this.shuffleItems();
      }
      this.apply();
    }

    shuffleItems() {
      const items = Array.from(this.grid.querySelectorAll(':scope > .grid__item'));
      if (items.length < 2) return;

      // Skip shuffle when the shopper picked an explicit Shopify sort.
      try {
        if (new URLSearchParams(window.location.search).has('sort_by')) return;
      } catch (e) {}

      const seed = this.getSessionSeed();
      const rand = this.mulberry32(this.hashSeed(seed));
      const order = items.slice();
      for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        const tmp = order[i];
        order[i] = order[j];
        order[j] = tmp;
      }
      const frag = document.createDocumentFragment();
      order.forEach((el) => frag.appendChild(el));
      this.grid.appendChild(frag);
    }

    getSessionSeed() {
      const key = 'hs-col-shuffle:' + (window.location.pathname || '/');
      try {
        let seed = sessionStorage.getItem(key);
        if (!seed) {
          seed = String(Date.now()) + '-' + Math.random().toString(36).slice(2);
          sessionStorage.setItem(key, seed);
        }
        return seed;
      } catch (e) {
        return String(Date.now());
      }
    }

    hashSeed(str) {
      let h = 2166136261;
      for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      return h >>> 0;
    }

    mulberry32(a) {
      return function () {
        let t = (a += 0x6d2b79f5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
    }

    getPageFromUrl() {
      const raw = new URLSearchParams(window.location.search).get('hs_page');
      const page = parseInt(raw || '1', 10);
      return Number.isFinite(page) && page > 0 ? page : 1;
    }

    buildUrl(page) {
      const url = new URL(window.location.href);
      if (page <= 1) {
        url.searchParams.delete('hs_page');
      } else {
        url.searchParams.set('hs_page', String(page));
      }
      // Never send Shopify's native page= while we paginate client-side off a large window.
      url.searchParams.delete('page');
      return url;
    }

    apply() {
      const all = Array.from(this.grid.querySelectorAll(':scope > .grid__item'));
      // Optional chip filters mark non-matches with data-hs-match="0".
      const items = all.filter((el) => el.getAttribute('data-hs-match') !== '0');
      all.forEach((el) => {
        el.hidden = true;
      });

      const total = items.length;
      const totalPages = Math.max(1, Math.ceil(total / this.perPage));
      let page = this.getPageFromUrl();
      if (page > totalPages) page = totalPages;
      if (page < 1) page = 1;

      const start = (page - 1) * this.perPage;
      const end = start + this.perPage;
      items.forEach((el, index) => {
        el.hidden = index < start || index >= end;
      });

      this.renderPager(page, totalPages);
      if (this.pager) this.pager.dataset.page = String(page);
    }

    renderPager(page, totalPages) {
      if (!this.pager) return;

      if (totalPages <= 1) {
        this.pager.hidden = true;
        this.pager.innerHTML = '';
        return;
      }

      this.pager.hidden = false;
      const label = this.dataset.paginationLabel || 'Pagination';
      const prevLabel = this.dataset.prevLabel || 'Previous';
      const nextLabel = this.dataset.nextLabel || 'Next';
      const pageLabelTemplate = this.dataset.pageLabel || 'Page __N__';
      const caret = this.pager.dataset.caretHtml || '';

      let html =
        '<nav class="pagination" role="navigation" aria-label="' +
        this.escapeAttr(label) +
        '"><ul class="pagination__list list-unstyled" role="list">';

      if (page > 1) {
        html +=
          '<li><a href="' +
          this.escapeAttr(this.buildUrl(page - 1).toString()) +
          '" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" aria-label="' +
          this.escapeAttr(prevLabel) +
          '"><span class="svg-wrapper">' +
          caret +
          '</span></a></li>';
      }

      for (let i = 1; i <= totalPages; i++) {
        const pageAria = pageLabelTemplate.replace(/__N__|\{\{\s*number\s*\}\}/g, String(i));
        html += '<li>';
        if (i === page) {
          html +=
            '<a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page" aria-label="' +
            this.escapeAttr(pageAria) +
            '">' +
            i +
            '</a>';
        } else {
          html +=
            '<a href="' +
            this.escapeAttr(this.buildUrl(i).toString()) +
            '" class="pagination__item link" aria-label="' +
            this.escapeAttr(pageAria) +
            '">' +
            i +
            '</a>';
        }
        html += '</li>';
      }

      if (page < totalPages) {
        html +=
          '<li><a href="' +
          this.escapeAttr(this.buildUrl(page + 1).toString()) +
          '" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" aria-label="' +
          this.escapeAttr(nextLabel) +
          '"><span class="svg-wrapper">' +
          caret +
          '</span></a></li>';
      }

      html += '</ul></nav>';
      this.pager.innerHTML = html;
    }

    escapeAttr(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }
  }

  customElements.define('hs-bestsellers-pager', HsBestsellersPager);
}
